import {
  getAnswerGrades,
  getAnswerOptedOut,
  getGradingOptOut,
  getGradingRows,
  getGradingScale,
  isAnswerableQuestionType,
} from '@onesource/schemas';
import type { TFormQuestion, TReviewAnswer, TSubmissionFeedback } from '@onesource/services';

/** One graded row of a grading question: "Breathing cues" → "4 / 5" (or the opt-out label). */
export interface TFeedbackGrade {
  rowId: string;
  label: string;
  value: string;
}

/** A teacher's answer to one feedback question, reduced to what a reader needs. */
export interface TFeedbackItem {
  question: TFormQuestion;
  /** Plain text: a written answer split into paragraphs, or the chosen options. */
  lines: string[];
  grades: TFeedbackGrade[];
  fileUrls: string[];
}

/** Question and option texts may hold editor HTML; feedback prints them as plain text. */
export const toPlainText = (html: string | null | undefined): string =>
  (html ?? '')
    .replace(/<\/?[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();

/** An accept question's "accepted", or a signature's JSON → what a reader should see. */
const consentText = (question: TFormQuestion, text: string): string | null => {
  if (question.questionType === 'accept') {
    return text === 'accepted' ? 'Accepted' : null;
  }
  try {
    const parsed = JSON.parse(text) as { checked?: boolean; name?: string };
    return parsed.checked ? `Accepted and signed by ${parsed.name?.trim() || 'the member'}` : null;
  } catch {
    return null;
  }
};

const toItem = (question: TFormQuestion, answer: TReviewAnswer | undefined): TFeedbackItem => {
  const item: TFeedbackItem = { question, lines: [], grades: [], fileUrls: answer?.fileUrls ?? [] };
  if (!answer) {
    return item;
  }
  const optionText = new Map(question.options.map(option => [option.id, toPlainText(option.optionText)]));
  const text = answer.textAnswer?.trim();
  if (text && (question.questionType === 'accept' || question.questionType === 'accept_with_signature')) {
    const consent = consentText(question, text);
    item.lines = consent ? [consent] : [];
  } else if (text) {
    item.lines = text
      .split(/\n+/)
      .map(line => line.trim())
      .filter(Boolean);
  } else if (answer.selectedOptionIds?.length) {
    item.lines = [
      answer.selectedOptionIds
        .map(id => optionText.get(id) ?? '')
        .filter(Boolean)
        .join(', '),
    ];
  } else if (answer.orderingSequence?.length) {
    item.lines = [
      answer.orderingSequence
        .map(id => optionText.get(id) ?? '')
        .filter(Boolean)
        .join(' → '),
    ];
  }

  const grades = getAnswerGrades(answer.value);
  const optedOut = new Set(getAnswerOptedOut(answer.value));
  const scale = getGradingScale(question.metadata);
  const optOutLabel = getGradingOptOut(question.metadata).label;
  item.grades = getGradingRows(question.metadata).flatMap(row => {
    if (typeof grades[row.id] === 'number') {
      return [{ rowId: row.id, label: row.label, value: `${grades[row.id]} / ${scale.max}` }];
    }
    return optedOut.has(row.id) ? [{ rowId: row.id, label: row.label, value: optOutLabel }] : [];
  });
  return item;
};

export const hasItemContent = (item: TFeedbackItem): boolean =>
  item.lines.length > 0 || item.grades.length > 0 || item.fileUrls.length > 0;

/**
 * Answers as a list of readable items, in form order. Text blocks are content,
 * not questions, and are left out.
 */
export const getAnswerItems = (questions: TFormQuestion[], answers: TReviewAnswer[]): TFeedbackItem[] => {
  const answersByQuestion = new Map(answers.map(a => [a.questionId, a]));
  return [...questions]
    .sort((a, b) => a.position - b.position)
    .filter(q => isAnswerableQuestionType(q.questionType))
    .map(q => toItem(q, answersByQuestion.get(q.id)));
};

/**
 * The teacher's feedback as a short list of answered questions. Questions the
 * teacher left blank are dropped — the student reads a note, not an empty form.
 * Shared by the form page and the PDF so both say the same thing.
 */
export const getFeedbackItems = (feedback: TSubmissionFeedback): TFeedbackItem[] =>
  getAnswerItems(feedback.feedbackForm.questions, feedback.answers).filter(hasItemContent);

/** Feedback edited after it was first shared. */
export const wasFeedbackUpdated = (feedback: Pick<TSubmissionFeedback, 'submittedAt' | 'updatedAt'>): boolean =>
  !!feedback.updatedAt && feedback.updatedAt !== feedback.submittedAt;

/** "https://cdn…/uploads/1712-notes.pdf" → "notes.pdf". */
export const fileNameFromUrl = (url: string): string => {
  const last = url.split('?')[0].split('/').reverse().find(Boolean) ?? url;
  try {
    return decodeURIComponent(last.replace(/^\d+-/, ''));
  } catch {
    return last;
  }
};
